// src/utils/rbac.js
// ===== RBAC MANAGER UTILITY =====
import { ROLES } from "../constants/roles.js";
import { expandPermissions } from "../constants/permissions.js";
import { PERMISSION_GROUPS, PERMISSIONS } from "../constants/permissions.js";

/**
 * RBAC Manager Class
 * Handles role-based access control logic
 */
export class RBACManager {
  constructor(userRole = null, customPermissions = []) {
    this.userRole = userRole;
    this.customPermissions = Array.isArray(customPermissions)
      ? customPermissions
      : [];
  }

  /**
   * Get user permissions based on role and custom permissions
   * @returns {string[]} Array of permission strings
   */
  getUserPermissions() {
    if (!this.userRole) return this.customPermissions;

    const role = ROLES[this.userRole];
    if (!role) {
      console.warn(`Unknown role: ${this.userRole}`);
      return this.customPermissions;
    }

    const rolePermissions = role.permissions || [];

    // Combine role permissions with custom permissions and remove duplicates
    const allPermissions = [
      ...new Set([...rolePermissions, ...this.customPermissions]),
    ];

    // Expand permissions to include dependencies
    return expandPermissions(allPermissions);
  }

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission to check
   * @returns {boolean} True if user has the permission
   */
  hasPermission(permission) {
    if (!permission) return false;

    const userPermissions = this.getUserPermissions();
    return userPermissions.includes(permission);
  }

  /**
   * Check if user has any of the provided permissions
   * @param {string[]} permissions - Array of permissions to check
   * @returns {boolean} True if user has at least one permission
   */
  hasAnyPermission(permissions = []) {
    if (!Array.isArray(permissions) || permissions.length === 0) return true;

    const userPermissions = this.getUserPermissions();
    return permissions.some((permission) =>
      userPermissions.includes(permission)
    );
  }

  /**
   * Check if user has all of the provided permissions
   * @param {string[]} permissions - Array of permissions to check
   * @returns {boolean} True if user has all permissions
   */
  hasAllPermissions(permissions = []) {
    if (!Array.isArray(permissions) || permissions.length === 0) return true;

    const userPermissions = this.getUserPermissions();
    return permissions.every((permission) =>
      userPermissions.includes(permission)
    );
  }

  /**
   * Check if user has a specific role
   * @param {string} role - Role to check
   * @returns {boolean} True if user has the role
   */
  hasRole(role) {
    return this.userRole === role;
  }

  /**
   * Check if user has any of the provided roles
   * @param {string[]} roles - Array of roles to check
   * @returns {boolean} True if user has at least one role
   */
  hasAnyRole(roles = []) {
    if (!Array.isArray(roles) || roles.length === 0) return true;
    return roles.includes(this.userRole);
  }

  /**
   * Get user role level (for hierarchical checks)
   * @returns {number} Role level (higher = more privileged)
   */
  getRoleLevel() {
    if (!this.userRole) return 0;

    const role = ROLES[this.userRole];
    return role?.level || 0;
  }

  /**
   * Check if user can manage another role
   * @param {string} targetRole - Role to manage
   * @returns {boolean} True if user can manage the target role
   */
  canManageRole(targetRole) {
    if (!this.userRole || !targetRole) return false;

    const userLevel = this.getRoleLevel();
    const targetRole_obj = ROLES[targetRole];
    const targetLevel = targetRole_obj?.level || 0;

    return userLevel > targetLevel;
  }

  /**
   * Get accessible routes based on user permissions
   * @param {object[]} routes - Array of route configurations
   * @returns {object[]} Array of accessible routes
   */
  getAccessibleRoutes(routes = []) {
    return routes.filter((route) => {
      if (!route.permissions || route.permissions.length === 0) return true;
      return this.hasAnyPermission(route.permissions);
    });
  }

  /**
   * Check if user can access a specific route
   * @param {object} route - Route configuration
   * @returns {boolean} True if user can access the route
   */
  canAccessRoute(route) {
    if (!route) return false;
    if (!route.permissions || route.permissions.length === 0) return true;

    return this.hasAnyPermission(route.permissions);
  }

  /**
   * Get sidebar routes that user can access
   * @param {object[]} routes - Array of route configurations
   * @returns {object[]} Array of accessible sidebar routes
   */
  getSidebarRoutes(routes = []) {
    return this.getAccessibleRoutes(routes).filter(
      (route) => route.sidebar === true
    );
  }

  /**
   * Check if user is admin (has admin-level role)
   * @returns {boolean} True if user is admin
   */
  isAdmin() {
    return this.hasAnyRole(["SUPER_ADMIN", "ADMIN"]);
  }

  /**
   * Check if user is super admin
   * @returns {boolean} True if user is super admin
   */
  isSuperAdmin() {
    return this.hasRole("SUPER_ADMIN");
  }

  /**
   * Check if user is regular user (non-admin)
   * @returns {boolean} True if user is regular user
   */
  isRegularUser() {
    return (
      this.hasAnyRole(["USER", "GUEST", "TEAM_LEAD", "MANAGER"]) &&
      !this.isAdmin()
    );
  }

  /**
   * Get role display information
   * @returns {object} Role display info
   */
  getRoleDisplayInfo() {
    if (!this.userRole) {
      return {
        name: "No Role",
        color: "#6c757d",
        icon: "fas fa-question-circle",
        level: 0,
      };
    }

    const role = ROLES[this.userRole];
    if (!role) {
      return {
        name: "Unknown Role",
        color: "#6c757d",
        icon: "fas fa-exclamation-triangle",
        level: 0,
      };
    }

    return {
      name: role.name,
      color: role.color,
      icon: role.icon,
      level: role.level,
      description: role.description,
    };
  }

  /**
   * Get permission groups that user has access to
   * @returns {object[]} Array of accessible permission groups
   */
  getAccessiblePermissionGroups() {
    const userPermissions = this.getUserPermissions();
    const accessibleGroups = [];

    Object.entries(PERMISSION_GROUPS || {}).forEach(([groupKey, group]) => {
      const hasAnyGroupPermission = group.permissions.some((permission) =>
        userPermissions.includes(permission)
      );

      if (hasAnyGroupPermission) {
        accessibleGroups.push({
          key: groupKey,
          ...group,
          accessiblePermissions: group.permissions.filter((permission) =>
            userPermissions.includes(permission)
          ),
        });
      }
    });

    return accessibleGroups;
  }

  /**
   * Check resource ownership
   * @param {object} resource - Resource object
   * @param {string} userIdField - Field name for user ID (default: 'userId')
   * @returns {boolean} True if user owns the resource
   */
  ownsResource(resource, userIdField = "userId") {
    if (!resource || !this.userId) return false;
    return resource[userIdField] === this.userId;
  }

  /**
   * Check if user can access resource (ownership or permission-based)
   * @param {object} resource - Resource object
   * @param {string[]} permissions - Required permissions for non-owners
   * @param {string} userIdField - Field name for user ID
   * @returns {boolean} True if user can access the resource
   */
  canAccessResource(resource, permissions = [], userIdField = "userId") {
    // Admins can access everything
    if (this.isAdmin()) return true;

    // Resource owners can access their own resources
    if (this.ownsResource(resource, userIdField)) return true;

    // Check permissions for non-owners
    return this.hasAnyPermission(permissions);
  }

  /**
   * Generate permission summary for debugging
   * @returns {object} Permission summary
   */
  getPermissionSummary() {
    return {
      role: this.userRole,
      roleLevel: this.getRoleLevel(),
      permissions: this.getUserPermissions(),
      customPermissions: this.customPermissions,
      isAdmin: this.isAdmin(),
      isSuperAdmin: this.isSuperAdmin(),
      isRegularUser: this.isRegularUser(),
      roleDisplayInfo: this.getRoleDisplayInfo(),
    };
  }

  /**
   * Validate RBAC configuration
   * @returns {object} Validation result
   */
  validateConfiguration() {
    const issues = [];

    // Check if role exists
    if (this.userRole && !ROLES[this.userRole]) {
      issues.push(`Role '${this.userRole}' not found in ROLES configuration`);
    }

    // Check if custom permissions are valid
    const validPermissions = Object.values(PERMISSIONS);
    this.customPermissions.forEach((permission) => {
      if (!validPermissions.includes(permission)) {
        issues.push(`Invalid custom permission: '${permission}'`);
      }
    });

    // Check role permissions
    if (this.userRole && ROLES[this.userRole]) {
      const rolePermissions = ROLES[this.userRole].permissions || [];
      rolePermissions.forEach((permission) => {
        if (!validPermissions.includes(permission)) {
          issues.push(
            `Invalid role permission: '${permission}' in role '${this.userRole}'`
          );
        }
      });
    }

    return {
      isValid: issues.length === 0,
      issues,
    };
  }

  /**
   * Clone RBAC manager with different role/permissions
   * @param {string} newRole - New role
   * @param {string[]} newCustomPermissions - New custom permissions
   * @returns {RBACManager} New RBAC manager instance
   */
  clone(
    newRole = this.userRole,
    newCustomPermissions = this.customPermissions
  ) {
    return new RBACManager(newRole, newCustomPermissions);
  }

  /**
   * Serialize RBAC manager for storage
   * @returns {object} Serializable object
   */
  serialize() {
    return {
      userRole: this.userRole,
      customPermissions: this.customPermissions,
      userId: this.userId,
    };
  }

  /**
   * Deserialize RBAC manager from stored data
   * @param {object} data - Serialized data
   * @returns {RBACManager} RBAC manager instance
   */
  static deserialize(data) {
    const rbac = new RBACManager(data.userRole, data.customPermissions);
    if (data.userId) {
      rbac.userId = data.userId;
    }
    return rbac;
  }

  toJSON() {
    return this.serialize(); // or return a plain object
  }
}

/**
 * Utility function to create RBAC manager
 * @param {string} role - User role
 * @param {string[]} customPermissions - Custom permissions
 * @returns {RBACManager} RBAC manager instance
 */
export const createRBACManager = (role, customPermissions = []) => {
  return new RBACManager(role, customPermissions);
};

/**
 * Check if a permission exists in the system
 * @param {string} permission - Permission to check
 * @returns {boolean} True if permission exists
 */
export const isValidPermission = (permission) => {
  return Object.values(PERMISSIONS).includes(permission);
};

/**
 * Check if a role exists in the system
 * @param {string} role - Role to check
 * @returns {boolean} True if role exists
 */
export const isValidRole = (role) => {
  return Object.keys(ROLES).includes(role);
};

/**
 * Get all available permissions
 * @returns {string[]} Array of all permissions
 */
export const getAllPermissions = () => {
  return Object.values(PERMISSIONS);
};

/**
 * Get all available roles
 * @returns {string[]} Array of all role keys
 */
export const getAllRoles = () => {
  return Object.keys(ROLES);
};

/**
 * Get role hierarchy (sorted by level)
 * @returns {object[]} Array of roles sorted by level (highest first)
 */
export const getRoleHierarchy = () => {
  return Object.entries(ROLES)
    .map(([key, role]) => ({ key, ...role }))
    .sort((a, b) => b.level - a.level);
};

// Export default RBAC manager
export default RBACManager;
